import React, { useState, useEffect } from 'react';
import { Search, Monitor, User, Building2, MapPin, MonitorPlay, Trash2, Cpu, HardDrive, Network, Printer, Edit2 } from 'lucide-react';
import adminSocketService from '../services/socketService';
import Cookies from 'js-cookie';
import { useConfirm } from '../context/ConfirmContext';
import { API_BASE_URL } from '../config';

const getAdminId = () => {
  return Cookies.get('adminId') || '6a6adad6068ce446beb73a07';
};

const formatBytes = (bytes) => {
  if (bytes === 0 || !bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const PcInfo = () => {
  const [devices, setDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loadingSysInfo, setLoadingSysInfo] = useState(false);
  const confirm = useConfirm();

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [isRoomDropdownOpen, setIsRoomDropdownOpen] = useState(false);
  const [isRowDropdownOpen, setIsRowDropdownOpen] = useState(false);
  const [departmentMaster, setDepartmentMaster] = useState([]);
  const [roomMaster, setRoomMaster] = useState([]);
  const [editForm, setEditForm] = useState({
    computerName: '',
    assignedUser: '',
    department: '',
    room: '',
    row: '',
    seat: ''
  });

  const fetchDevices = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/devices?adminId=${getAdminId()}`);
      if (response.ok) {
        const data = await response.json();
        setDevices(data);
        if (data.length > 0 && !selectedDevice) {
          setSelectedDevice(data[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch devices', err);
    }
  };

  useEffect(() => {
    fetchDevices();

    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/departments?adminId=${getAdminId()}`);
        if (response.ok) {
          const data = await response.json();
          setDepartmentMaster(data);
        }
      } catch (err) {
        console.error('Failed to fetch departments', err);
      }
    };
    
    const fetchRooms = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/rooms?adminId=${getAdminId()}`);
        if (response.ok) {
          const data = await response.json();
          setRoomMaster(data);
        }
      } catch (err) {
        console.error('Failed to fetch rooms', err);
      }
    };

    fetchDepartments();
    fetchRooms();
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      const fetchSystemInfo = async () => {
        setLoadingSysInfo(true);
        try {
          const response = await fetch(`${API_BASE_URL}/devices/${selectedDevice.deviceId}/system-info`);
          if (response.ok) {
            const data = await response.json();
            setSystemInfo(data);
          } else {
            setSystemInfo(null);
          }
        } catch (err) {
          console.error('Failed to fetch system info', err);
          setSystemInfo(null);
        } finally {
          setLoadingSysInfo(false);
        }
      };
      fetchSystemInfo();
    }
  }, [selectedDevice]);

  const handleOpenEditModal = () => {
    if (!selectedDevice) return;
    setEditForm({
      computerName: selectedDevice.computerName || '',
      assignedUser: selectedDevice.assignedUser || '',
      department: selectedDevice.department || '',
      room: selectedDevice.room || '',
      row: selectedDevice.row || '',
      seat: selectedDevice.seat || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    const socket = adminSocketService.connect();
    if (selectedDevice && socket) {
      socket.emit('rename-device', {
        deviceId: selectedDevice.deviceId,
        newName: editForm.computerName,
        assignedUser: editForm.assignedUser,
        department: editForm.department,
        room: editForm.room,
        row: editForm.row,
        seat: editForm.seat
      });
      setSelectedDevice({ ...selectedDevice, ...editForm });
      setIsEditModalOpen(false);
      setTimeout(fetchDevices, 500);
    }
  };

  const handleDeleteDevice = async () => {
    const isConfirmed = await confirm(`Are you sure you want to completely delete ${selectedDevice?.computerName}? This will force logout the agent.`);
    if (selectedDevice && isConfirmed) {
      const socket = adminSocketService.connect();
      if (socket) {
        socket.emit('delete-device-permanent', { deviceId: selectedDevice.deviceId });
        setSelectedDevice(null);
        setTimeout(fetchDevices, 500);
      }
    }
  };

  const handleActivateDevice = async () => {
    const isConfirmed = await confirm(`Re-activate ${selectedDevice?.computerName}?`);
    if (selectedDevice && isConfirmed) {
      const socket = adminSocketService.connect();
      if (socket) {
        socket.emit('activate-device', { deviceId: selectedDevice.deviceId });
        setSelectedDevice({ ...selectedDevice, isActive: true });
        setTimeout(fetchDevices, 500);
      }
    }
  };

  // Derive unique departments for filter dropdown
  const departments = ['All', ...Array.from(new Set(devices.map(d => d.department).filter(Boolean)))];

  // Filter devices
  const filteredDevices = devices.filter(d => {
    // Hide inactive unless showInactive is checked
    if (!showInactive && d.isActive === false) return false;

    // Dept Filter
    if (deptFilter !== 'All' && d.department !== deptFilter) return false;

    // Search Filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchName = d.computerName && d.computerName.toLowerCase().includes(query);
      const matchUser = d.assignedUser && d.assignedUser.toLowerCase().includes(query);
      const matchDept = d.department && d.department.toLowerCase().includes(query);
      const matchRoom = d.room && d.room.toLowerCase().includes(query);
      const matchIp = d.localIpAddress && d.localIpAddress.toLowerCase().includes(query);
      return matchName || matchUser || matchDept || matchRoom || matchIp;
    }

    return true;
  });

  // Extract specs from systemInfo
  const os = systemInfo?.os || {};
  const sys = systemInfo?.system || {};
  const cpu = systemInfo?.cpu || {};
  const bios = systemInfo?.bios || {};
  const mobo = systemInfo?.baseboard || {};
  const ram = systemInfo?.mem || {};
  const disks = systemInfo?.diskLayout || [];
  const fsSize = systemInfo?.fsSize || [];
  const gpu = systemInfo?.gpu?.controllers || [];
  const displays = systemInfo?.gpu?.displays || [];
  const net = systemInfo?.networkInterfaces || [];
  const primaryNet = net.find(n => n.ip4 && n.ip4 !== '127.0.0.1') || net[0] || {};
  const printers = systemInfo?.printer || [];
  const usb = systemInfo?.usb || [];
  const scanners = usb.filter(u => u.name && u.name.toLowerCase().includes('scanner')).map(u => u.name);

  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--bg-main)', overflow: 'hidden' }}>
      {/* Left Pane - Device List */}
      <div style={{ width: 320, background: 'var(--bg-card)', borderRight: '1px solid var(--border-light)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 20px 14px 20px', borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Monitor size={18} color="var(--accent-primary)" />
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>PC Directory</h2>
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent-primary)', background: 'rgba(var(--app-primary-color-rgb), 0.15)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)', padding: '2px 8px', borderRadius: 12 }}>
              {filteredDevices.length} PCs
            </span>
          </div>

          <div style={{ position: 'relative', marginBottom: 12 }}>
            <Search size={14} color="var(--accent-primary)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search name, user, model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '9px 10px 9px 34px',
                color: 'var(--text-primary)',
                fontSize: 12,
                outline: 'none',
                transition: 'border 0.2s'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
              onBlur={e => e.target.style.borderColor = 'var(--border-light)'}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>DEPARTMENT FILTER</label>
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                color: 'var(--text-primary)',
                padding: '8px 10px',
                borderRadius: 6,
                fontSize: 12,
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {departments.map(d => <option key={d} value={d} style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>{d}</option>)}
            </select>
          </div>
        </div>

        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border-light)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            <input 
              type="checkbox" 
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              style={{ accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
            />
            Show Deleted / Dormant PCs
          </label>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredDevices.map(device => {
            const isSelected = selectedDevice?._id === device._id;
            return (
              <div 
                key={device._id}
                onClick={() => setSelectedDevice(device)}
                style={{
                  background: isSelected ? 'rgba(var(--app-primary-color-rgb), 0.18)' : 'var(--input-bg)',
                  border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-light)'}`,
                  borderRadius: 10,
                  padding: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {isSelected && (
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: 'var(--accent-primary)' }} />
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{device.computerName}</h3>
                  {device.department && (
                    <div style={{ background: 'rgba(var(--app-primary-color-rgb), 0.15)', color: 'var(--accent-primary)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800 }}>
                      {device.department.toUpperCase()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>
                    <User size={12} color="var(--accent-primary)" />
                    {device.assignedUser || 'Unassigned'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
                    {device.localIpAddress || 'N/A'}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-light)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: 11 }}>
                    <MapPin size={11} color="var(--accent-primary)" />
                    {device.room || 'Unknown Room'}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: device.isActive === false ? '#EF4444' : (device.isOnline ? '#22C55E' : 'var(--text-muted)') }}>
                    {device.isActive === false ? '● DORMANT' : (device.isOnline ? '● ONLINE' : '○ OFFLINE')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Pane - Details */}
      <div style={{ flex: 1, padding: 32, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 32 }}>
        {!selectedDevice ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: 'var(--text-muted)' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(var(--app-primary-color-rgb), 0.12)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Monitor size={32} color="var(--accent-primary)" />
            </div>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>Select a Workstation</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', maxWidth: 340 }}>
                Choose any PC from the directory on the left to inspect hardware specs, operating system build, disk space, and network details.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(var(--app-primary-color-rgb), 0.12)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Monitor size={24} color="var(--accent-primary)" />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{selectedDevice.computerName} Monitor</h1>
                      <div style={{ background: selectedDevice.isActive === false ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)', color: selectedDevice.isActive === false ? '#EF4444' : '#22C55E', border: `1px solid ${selectedDevice.isActive === false ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }}>
                        {selectedDevice.isActive === false ? 'DORMANT' : (selectedDevice.isOnline ? 'ACTIVE' : 'OFFLINE')}
                      </div>
                    </div>
                    <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>model: <span style={{ color: 'var(--text-primary)' }}>{sys.model || 'Unknown'}</span></p>
                  </div>
                </div>

                {/* Edit & Delete Buttons */}
                <div style={{ display: 'flex', gap: 12 }}>
                  {selectedDevice.isActive === false ? (
                    <button onClick={handleActivateDevice} style={{ background: 'rgba(34, 197, 94, 0.15)', border: '1px solid #22C55E', color: 'var(--text-primary)', borderRadius: 24, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, transition: 'all 0.2s ease' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(34, 197, 94, 0.25)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(34, 197, 94, 0.15)'}>
                      <MonitorPlay size={14} color="#22C55E" /> Activate
                    </button>
                  ) : (
                    <>
                      <button onClick={handleOpenEditModal} style={{ background: 'rgba(var(--app-primary-color-rgb), 0.15)', border: '1px solid var(--accent-primary)', color: 'var(--text-primary)', borderRadius: 24, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, transition: 'all 0.2s ease' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.25)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.15)'}>
                        <Edit2 size={14} color="var(--accent-primary)" /> Edit
                      </button>
                      <button onClick={handleDeleteDevice} style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #EF4444', color: 'var(--text-primary)', borderRadius: 24, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, transition: 'all 0.2s ease' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'}>
                        <Trash2 size={14} color="#EF4444" /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Info Grid */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 20, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {[
                { icon: User, label: 'ASSIGNED EMPLOYEE', value: selectedDevice.assignedUser || 'N/A' },
                { icon: Building2, label: 'DEPARTMENT', value: selectedDevice.department || 'N/A', badge: true },
                { icon: MapPin, label: 'ROOM / LAB LOCATION', value: selectedDevice.room || 'N/A' },
                { icon: MonitorPlay, label: 'ROW & SEAT LAYOUT', value: (selectedDevice.row && selectedDevice.seat) ? `Row ${selectedDevice.row} / Seat ${selectedDevice.seat}` : 'N/A' }
              ].map((info, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 16, borderRight: idx < 3 ? '1px solid var(--border-light)' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(var(--app-primary-color-rgb), 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <info.icon size={16} color="var(--accent-primary)" />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: '0.05em' }}>{info.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {info.badge ? (
                        <span style={{ background: 'rgba(var(--app-primary-color-rgb), 0.15)', color: 'var(--accent-primary)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{info.value}</span>
                      ) : info.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {loadingSysInfo ? (
              <div style={{ color: 'var(--text-muted)', padding: 32 }}>Loading system specs...</div>
            ) : !systemInfo ? (
              <div style={{ color: 'var(--text-muted)', padding: 32 }}>No system specs available for this device.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* SYSTEM & OS */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 24 }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: 14, fontWeight: 800, color: 'var(--accent-primary)', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase' }}>
                    <Monitor size={16} color="var(--accent-primary)" /> SYSTEM & OS
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>OS VERSION</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{os.distro} {os.release} ({os.arch})</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>BUILD</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{os.build || 'N/A'}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>COMPUTER NAME</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{selectedDevice.computerName}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SYSTEM MANUFACTURER</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{sys.manufacturer || 'N/A'}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SYSTEM MODEL</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{sys.model || 'N/A'}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SERIAL NUMBER</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{sys.serial || mobo.serial || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* PROCESSOR & MOTHERBOARD */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 24 }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: 14, fontWeight: 700, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase' }}>
                    <Cpu size={16} /> PROCESSOR & MOTHERBOARD
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>CPU</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{cpu.manufacturer} {cpu.brand}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>CORES</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{cpu.physicalCores} Physical / {cpu.cores} Logical</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SPEED</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{cpu.speed || cpu.speedMax} GHz</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>BIOS</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{bios.vendor} {bios.version}</div>
                    </div>
                  </div>
                </div>

                {/* MEMORY & STORAGE */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 24 }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: 14, fontWeight: 700, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase' }}>
                    <HardDrive size={16} /> MEMORY & STORAGE
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>TOTAL RAM</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>{formatBytes(ram.total)}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>FREE RAM</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>{formatBytes(ram.free)}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>TOTAL STORAGE</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>
                        {fsSize.length > 0 ? formatBytes(fsSize.reduce((a, f) => a + f.size, 0)) : 'N/A'}
                      </div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>FREE STORAGE</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>
                        {fsSize.length > 0 ? formatBytes(fsSize.reduce((a, f) => a + f.available, 0)) : 'N/A'}
                      </div>
                    </div>
                    {disks.map((d, i) => (
                      <div key={i} style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>DISK {i + 1} ({d.type || 'Unknown'})</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>{d.name || d.device} - {formatBytes(d.size)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* GRAPHICS & DISPLAYS */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 24 }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: 14, fontWeight: 700, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase' }}>
                    <MonitorPlay size={16} /> GRAPHICS & DISPLAYS
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    {gpu.map((g, i) => (
                      <div key={i} style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>GPU {i + 1}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>{g.vendor} {g.model} (VRAM: {g.vram || 'Unknown'} MB)</div>
                      </div>
                    ))}
                    {displays.map((d, i) => (
                      <div key={i} style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>MONITOR {i + 1}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>{d.model || d.deviceName || 'Generic'} ({d.resolutionX}x{d.resolutionY})</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* NETWORK */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 24 }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: 14, fontWeight: 700, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase' }}>
                    <Network size={16} /> NETWORK
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>MAC ADDRESS</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{primaryNet.mac || 'N/A'}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>IP ADDRESS</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{primaryNet.ip4 || 'N/A'}</div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>INTERFACE</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{primaryNet.ifaceName || primaryNet.iface || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* PRINTERS & SCANNERS */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12, padding: 24 }}>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: 14, fontWeight: 700, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase' }}>
                    <Printer size={16} /> PRINTERS & SCANNERS
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>PRINTERS</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                        {printers.length > 0 ? printers.map(p => p.name).join(', ') : 'None detected'}
                      </div>
                    </div>
                    <div style={{ background: 'var(--input-bg)', padding: 16, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SCANNERS / IMAGING</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                        {scanners.length > 0 ? scanners.join(', ') : 'None detected'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal Overlay */}
      {isEditModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, width: 480, padding: 32, border: '1px solid var(--border-light)', boxShadow: '0 24px 48px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(var(--app-primary-color-rgb), 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Edit2 size={20} color="var(--accent-primary)" />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Edit Computer Details</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>Update workstation assignment and location info</p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.05em' }}>COMPUTER NAME</div>
                <input type="text" value={editForm.computerName} onChange={e => setEditForm({...editForm, computerName: e.target.value})} style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '12px 16px', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.05em' }}>ASSIGNED USER / NOTE</div>
                <input type="text" value={editForm.assignedUser} onChange={e => setEditForm({...editForm, assignedUser: e.target.value})} style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '12px 16px', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.05em' }}>DEPARTMENT</div>
                <div 
                  onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
                  style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '12px 16px', color: editForm.department ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 14, boxSizing: 'border-box', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  {editForm.department || 'Select Department'}
                  <span style={{ transform: isDeptDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 12 }}>▼</span>
                </div>
                {isDeptDropdownOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 8, overflow: 'hidden', zIndex: 10, boxShadow: '0 8px 16px rgba(0,0,0,0.3)' }}>
                    <div 
                      onClick={() => { setEditForm({...editForm, department: ''}); setIsDeptDropdownOpen(false); }}
                      style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 14, borderBottom: '1px solid var(--border-light)', transition: 'background 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    >
                      -- Remove Department --
                    </div>
                    {departmentMaster.map((dept, i) => (
                      <div 
                        key={i} 
                        onClick={() => { setEditForm({...editForm, department: dept.name}); setIsDeptDropdownOpen(false); }}
                        style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, transition: 'background 0.2s' }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {dept.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.05em' }}>ROOM / LAB</div>
                <div 
                  onClick={() => { setIsRoomDropdownOpen(!isRoomDropdownOpen); setIsDeptDropdownOpen(false); setIsRowDropdownOpen(false); }}
                  style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '12px 16px', color: editForm.room ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 14, boxSizing: 'border-box', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  {editForm.room || 'Select Room / Lab'}
                  <span style={{ transform: isRoomDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 12 }}>▼</span>
                </div>
                {isRoomDropdownOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 8, overflow: 'hidden', zIndex: 10, boxShadow: '0 8px 16px rgba(0,0,0,0.3)', maxHeight: 200, overflowY: 'auto' }}>
                    <div 
                      onClick={() => { setEditForm({...editForm, room: '', row: ''}); setIsRoomDropdownOpen(false); }}
                      style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 14, borderBottom: '1px solid var(--border-light)', transition: 'background 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    >
                      -- Remove Room --
                    </div>
                    {roomMaster.map((room, i) => (
                      <div 
                        key={i} 
                        onClick={() => { setEditForm({...editForm, room: room.name, row: ''}); setIsRoomDropdownOpen(false); }}
                        style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, transition: 'background 0.2s' }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {room.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ position: 'relative' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.05em' }}>ROW</div>
                  <div 
                    onClick={() => { setIsRowDropdownOpen(!isRowDropdownOpen); setIsRoomDropdownOpen(false); setIsDeptDropdownOpen(false); }}
                    style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '12px 16px', color: editForm.row ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 14, boxSizing: 'border-box', cursor: editForm.room ? 'pointer' : 'not-allowed', opacity: editForm.room ? 1 : 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {editForm.row ? `Row ${editForm.row}` : 'Select Row'}
                    <span style={{ transform: isRowDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 12 }}>▼</span>
                  </div>
                  {isRowDropdownOpen && editForm.room && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 8, overflow: 'hidden', zIndex: 10, boxShadow: '0 8px 16px rgba(0,0,0,0.3)', maxHeight: 150, overflowY: 'auto' }}>
                      <div 
                        onClick={() => { setEditForm({...editForm, row: ''}); setIsRowDropdownOpen(false); }}
                        style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 14, borderBottom: '1px solid var(--border-light)', transition: 'background 0.2s' }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        -- Remove Row --
                      </div>
                      {(() => {
                        const selectedRoomObj = roomMaster.find(r => r.name === editForm.room);
                        const numRows = selectedRoomObj ? parseInt(selectedRoomObj.totalRows, 10) : 0;
                        return Array.from({ length: numRows || 0 }, (_, i) => i + 1).map(rowNum => (
                          <div 
                            key={rowNum} 
                            onClick={() => { setEditForm({...editForm, row: rowNum.toString()}); setIsRowDropdownOpen(false); }}
                            style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Row {rowNum}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.05em' }}>SEAT NO.</div>
                  <input type="text" value={editForm.seat} onChange={e => setEditForm({...editForm, seat: e.target.value})} style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '12px 16px', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16, marginTop: 32 }}>
              <button onClick={() => setIsEditModalOpen(false)} style={{ background: 'var(--input-bg)', border: '1px solid var(--border-light)', color: 'var(--text-primary)', borderRadius: 8, padding: '12px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 600, transition: 'background 0.2s', flex: 1 }}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="btn-primary" style={{ borderRadius: 8, padding: '12px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s', flex: 1 }}>
                <Edit2 size={16} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PcInfo;
